'use client';

import styles from './ChatItem.module.css';
import type { ChatItemProps } from './ChatItem.types';
import { DOCUMENT_ANSWER_PREFIX } from '@/_constants/documents';
import { Flex, Flair, Avatar, Text } from '@/_components';
import { UserContext } from '@/_contexts/UserProvider';
import { getClasses } from '@/_utils/classnames';
import { getDateAgo, moment } from '@/_utils/dates';
import { removeHtml } from '@/_utils/sanitize';
import { truncateString } from '@/_utils/strings';
import { useMemo, useContext, type FC } from 'react';
import { useParams, useRouter } from 'next/navigation';
import type { Message } from '@/_types/models/conversations';

export const ChatItem: FC<ChatItemProps> = ({
  isActive,
  conversation,
  isAdvisor,
  name,
  type,
}) => {
  const { push } = useRouter();
  const { companyHash } = useParams();

  const { selectedCompanyHash, user } = useContext(UserContext);

  const lastMessageText: string = useMemo(() => {
    // not in the conversation
    if (!!conversation?.last_message_at && !conversation?.last_message) {
      return `Last message: ${moment(conversation?.last_message_at).format('LL')}`;
    }

    // no last message
    if (!conversation?.last_message?.text) return '';

    const isAttachment = conversation?.last_message?.text?.startsWith?.(
      DOCUMENT_ANSWER_PREFIX,
    );
    if (isAttachment) return 'Attachment';

    return removeHtml(conversation?.last_message?.text);
  }, [conversation?.last_message, conversation?.last_message_at]);

  const pathHash: string | undefined = useMemo(() => {
    if (!isAdvisor) return companyHash?.toString() || selectedCompanyHash;

    return conversation?.relevant_company_hash_id || companyHash?.toString();
  }, [
    companyHash,
    isAdvisor,
    selectedCompanyHash,
    conversation?.relevant_company_hash_id,
  ]);

  const path: string = useMemo(
    () =>
      isAdvisor
        ? `/admin/${pathHash}/messages/${conversation.id}`
        : `/inbox/${pathHash}/${conversation.id}`,
    [isAdvisor, pathHash, conversation.id],
  );

  const unreadCount: number = useMemo(
    () =>
      conversation?.unseen_messages?.filter(
        (um: Message) =>
          !um?.is_log && um?.unseen_user_ids?.includes?.(user?.id ?? -1),
      )?.length ?? 0,
    [conversation?.unseen_messages, user],
  );

  if (!conversation || !pathHash) return null;

  const daysAgo = getDateAgo(conversation?.last_message?.created_at);
  const chatItemClasses: string = getClasses(styles.chatItem, {
    [styles.chatItemActive]: isActive,
  });

  const limitedLastMessage: string = truncateString(
    lastMessageText || 'Empty Conversation',
    48,
  );

  const onReload = () => push(path);

  return (
    <div
      className={chatItemClasses}
      data-component="ChatItem"
      onClick={onReload}
    >
      <Flex gap={4}>
        <Avatar size={32} name={name} type={type}></Avatar>
        <Flex isColumn={true} className="wp-100">
          <Flex gap={10} isBetween={true}>
            <Text lineClamp={2} type="small">
              <b>{name}</b>
            </Text>
            {conversation?.last_message?.created_at && (
              <Text
                color="secondary"
                className={styles.chatItem__date}
                textAlign="right"
                type="tiny"
              >
                {daysAgo}
              </Text>
            )}
          </Flex>
          <Flex className={styles.chatItem__line} isBetween={true}>
            <Flex gap={10} isBetween={true}>
              <Text
                color={lastMessageText ? 'primary' : 'disabled'}
                type="small"
              >
                {limitedLastMessage}
              </Text>
            </Flex>
            <Flair count={unreadCount} />
          </Flex>
        </Flex>
      </Flex>
    </div>
  );
};
